"use client";

import { MouseEvent, useState, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import LockIcon from "@mui/icons-material/Lock";
import { TbLassoPolygon } from "react-icons/tb";
import { MoreVertical, Plus, Zap, Printer } from "lucide-react";

import { useStyles } from "./styles";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { addAlertDialog, getNewGenerations } from "@/redux/actions";
import { useGenContext } from "@/context/GenContext";
import { AlertDialogType } from "@/types/alert";
import { Image as TImage } from "@/types/thread";
import { CtaVersionType, postCtaConversion, postCtaView } from "utils/cta";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Image from "components/common/Image";
import CopyImage from "components/common/CopyImage";
import DownloadButton from "components/common/DownloadButton";
import RatingControls from "components/social/ratings/RatingControls";
import EnhanceImage from "components/idea/EnhanceImage";
import InpaintModal from "@/components/idea/GenInputs/InpaintModal";
import PaywallA from "@/components/common/PaywallsMicrotransactions/PaywallCTA/GenCardPaywall/PaywallA";
import PaywallB from "@/components/common/PaywallsMicrotransactions/PaywallCTA/GenCardPaywall/PaywallB";

function getImageWidth(isSingleImage: boolean, isLargeScreen: boolean) {
  return isSingleImage
    ? isLargeScreen
      ? 600
      : 300
    : isLargeScreen
    ? 300
    : 180;
}

interface GenerationCardProps {
  image: TImage;
  onImageClick: () => void;
  editEnabled?: boolean;
  isSingleImage?: boolean;
  ctaVersion?: CtaVersionType | null;
  imageIndex?: number;
  totalImages?: number;
}

const GenerationCard: React.FC<GenerationCardProps> = ({
  image,
  onImageClick,
  editEnabled = false,
  isSingleImage = false,
  ctaVersion,
  imageIndex = 0,
  totalImages = 1,
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { dispatch: genDispatch, addImage } = useGenContext();

  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");
  const hasActiveJobs = Boolean(user?.activeJobs && user.activeJobs.length > 0);

  const isLargeScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.up("lg")
  );
  const imageWidth = getImageWidth(isSingleImage, isLargeScreen);
  const { classes } = useStyles({ singleImage: isSingleImage });

  const [imageLoaded, setImageLoaded] = useState(false);
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isUpscaling, setIsUpscaling] = useState(false);

  const lastUpscaleTimeRef = useRef(0);
  const upscaleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showMenu = Boolean(anchorEl);

  const shouldLockImage = () => {
    if (isSubscribed) return false;
    if (totalImages === 4) {
      return imageIndex >= 2;
    }
    return Boolean(image.locked);
  };

  const locked = shouldLockImage();
  const [showSpotlightModal, setShowSpotlightModal] = useState(false);

  const handleImageLoad = () => setImageLoaded(true);

  const openMenu = (event: MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handlePlaceholderClick = async () => {
    setShowSpotlightModal(true);
    if (ctaVersion?.versionIdentifier) {
      await postCtaView(
        "locked_generations_on_click",
        ctaVersion.versionIdentifier
      );
    }
  };

  const handleUpgrade = async () => {
    setShowSpotlightModal(false);
    if (ctaVersion?.versionIdentifier) {
      await postCtaConversion(
        "locked_generations_on_click",
        ctaVersion.versionIdentifier
      );
    }
    router.push("/subscribe");
  };

  const handleEditImage = (e: MouseEvent<HTMLElement>, useInpaint: boolean) => {
    console.info(image.url);
    e.stopPropagation();
    handleCloseMenu();

    if (useInpaint) {
      genDispatch({ type: "SET_SELECTED_IMAGE", payload: image.url });
      genDispatch({ type: "SET_IS_EDITING_MASK", payload: true });
    } else {
      addImage(image.url);
    }
  };

  const handleShowEnhanceModal = () => {
    handleCloseMenu();
    setShowEnhanceModal(true);
  };

  const resetUpscaleState = useCallback(() => {
    setIsUpscaling(false);
    if (upscaleTimeoutRef.current) {
      clearTimeout(upscaleTimeoutRef.current);
      upscaleTimeoutRef.current = null;
    }
  }, []);

  const handleUpscaleImage = useCallback(async () => {
    handleCloseMenu();

    const now = Date.now();
    const timeSinceLastUpscale = now - lastUpscaleTimeRef.current;

    if (timeSinceLastUpscale < 2000) {
      console.log("Rejecting upscale - too soon after last submission");
      return;
    }

    if (isUpscaling || hasActiveJobs) {
      console.log("Rejecting upscale - already in progress");
      return;
    }

    if (!isSubscribed) {
      dispatch(
        addAlertDialog({
          title: "Subscription Required",
          description: "Upgrade to upscale this image!",
          nextButtonText: "Upgrade",
          type: AlertDialogType.SubscriptionRequired,
        })
      );
      return;
    }

    lastUpscaleTimeRef.current = now;
    setIsUpscaling(true);

    if (upscaleTimeoutRef.current) {
      clearTimeout(upscaleTimeoutRef.current);
    }

    upscaleTimeoutRef.current = setTimeout(() => {
      resetUpscaleState();
    }, 30000);

    try {
      await dispatch(
        getNewGenerations({ upscale: true, imageId: image.id })
      ).unwrap();
      setTimeout(() => {
        resetUpscaleState();
      }, 1000);
    } catch (error) {
      console.error("Error in handleUpscaleImage:", error);
      resetUpscaleState();
    }
  }, [
    dispatch,
    image.id,
    isSubscribed,
    isUpscaling,
    hasActiveJobs,
    resetUpscaleState,
  ]);

  if (locked) {
    return (
      <div className={classes.container}>
        <div
          className={classes.lockedImageWrapper}
          onClick={handlePlaceholderClick}
        >
          <Image
            src={image.url}
            alt={image.alt || ""}
            width={imageWidth}
            height={imageWidth}
            onLoad={handleImageLoad}
            style={{
              objectFit: "contain",
              borderRadius: "4px",
              filter: "blur(1rem)",
              WebkitFilter: "blur(1rem)",
            }}
            className={classes.blurredImage}
          />
          <div className={classes.lockIconWrapper}>
            <LockIcon className={classes.lockIcon} />
          </div>
        </div>

        {showSpotlightModal &&
          ctaVersion &&
          ctaVersion.versionIdentifier &&
          (ctaVersion.versionIdentifier.toUpperCase().startsWith("A") ? (
            <PaywallA
              onClose={() => setShowSpotlightModal(false)}
              onUpgrade={handleUpgrade}
              versionIdentifier={ctaVersion.versionIdentifier}
              ctaContent={ctaVersion.content}
            />
          ) : (
            <PaywallB
              onClose={() => setShowSpotlightModal(false)}
              onUpgrade={handleUpgrade}
              versionIdentifier={ctaVersion.versionIdentifier}
              ctaContent={ctaVersion.content}
            />
          ))}
      </div>
    );
  }

  return (
    <>
      <div className={classes.container}>
        <Image
          className={classes.image}
          src={image.url}
          alt={image.alt || ""}
          width={imageWidth}
          height={imageWidth}
          onLoad={handleImageLoad}
          onClick={onImageClick}
        />

        {imageLoaded && (
          <div className={classes.controls}>
            <RatingControls
              likesCount={image.likesCount}
              userRating={image.userRating}
              imageId={image.id}
            />
            <CopyImage imageUrl={image.url} color="white" />
            {isSubscribed && <DownloadButton url={image.url} color="white" />}

            {editEnabled && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton aria-label="Edit Controls">
                      <MoreVertical className="tw:h-6 tw:w-6 tw:text-white tw:filter tw:drop-shadow-[0px_0px_1px_#1E222A] tw:hover:scale-110" />
                    </IconButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={(e) => handleEditImage(e, false)}
                    >
                      <span className="tw:flex-1">Add to Prompt</span>
                      <Plus className="tw:ml-2 tw:h-4 tw:w-4" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => handleEditImage(e, true)}>
                      <span className="tw:flex-1">Replace Area</span>
                      <TbLassoPolygon className="tw:ml-2 tw:h-4 tw:w-4" />
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShowEnhanceModal}>
                      <span className="tw:flex-1">Enhance</span>
                      <Zap className="tw:ml-2 tw:h-4 tw:w-4" />
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleUpscaleImage}
                      disabled={isUpscaling || hasActiveJobs}
                    >
                      <span className="tw:flex-1">
                        {isUpscaling ? "Upscaling..." : "Upscale"}
                      </span>
                      <Printer className="tw:ml-2 tw:h-4 tw:w-4" />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <EnhanceImage
                  image={image}
                  modalOpen={showEnhanceModal}
                  setModalOpen={setShowEnhanceModal}
                  hideIcon
                />
              </>
            )}
          </div>
        )}
      </div>

      {editEnabled && <InpaintModal />}
    </>
  );
};

export default GenerationCard;
